import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as booking } from "./assets/icons/link icon.svg";

import { ReactComponent as dashboard } from "./assets/icons/Dashboard_new.svg";
import { ReactComponent as results } from "./assets/icons/Results_new.svg";
import { ReactComponent as settings } from "./assets/icons/Account_new.svg";
import { ReactComponent as assessment } from "./assets/icons/Activate_new.svg";
import { ReactComponent as support } from "./assets/icons/Support_new.svg";
import { ReactComponent as appointment } from "./assets/icons/appointment.svg";
import {
  bloodCholesterol,
  bloodEnergy,
  bloodGeneral,
  bloodHeart,
  bloodThyroid,
  bloodVitamins,
  bloodWeight,
  bloodMaleSexHealth,
  bloodFemaleSexHealth,
  bloodMenopause,
  bloodPolycystic,
  bloodVitaminB12,
  bloodTestosterone,
  bloodErectile,
  dnaWeight,
  dnaGlucose,
  dnaIntolerances,
  dnaHeart,
  dnaVitamins,
  bloodFertility,
  bloodProgresterone,
  bootsThyroid,
  bootsCholesterol,
  bootsEnergy,
  bootsGeneral,
  bootsVitamins,
  bootsWeight,
} from "./utils/constants";
import PropTypes from "prop-types";

/**
 * Filters an array to only include unique values.
 * Usage: [1, 2, 3, 2, 4].filter(filterUnique) > [1, 2, 3, 4]
 */
export const filterUnique = (value, index, array) =>
  array?.indexOf(value) === index;

/**
 * Pauses execution for a specified duration (in milliseconds).
 * Usage: await sleep(1000)
 */
export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Converts a string with underscores or hyphens to a title format.
 * Usage: asTitle('what_ever') > 'What Ever'
 */
export const asTitle = s =>
  (s || "")
    .replace(/[ _-]/g, " ")
    .replace(/([ _-]|\b)\w/g, l => l.toUpperCase())
    .replace(/\bdna\b/gi, "DNA");

/**
 * Checks if the user is on a mobile device.
 */
export const onMobile = /mobile|ip(hone|od|ad)|android|blackberry|opera mini/i.test(
  navigator.userAgent
);

/**
 * Custom error class that includes additional details.
 */
export class DetailedError extends Error {
  constructor(message, details) {
    super(message);
    this.name = "DetailedError";
    this.details = details;
  }
}

/**
 * Dynamically loads a script into the document.
 * Usage: await load_script('https://www.example.com/script.js')
 */
export const load_script = (url, allowed_origins = []) => {
  return new Promise((resolve, reject) => {
    let parsed;
    try {
      // Resolves relative URLs against the current origin too
      parsed = new URL(url, window.location.href);
    } catch (e) {
      reject(new Error(`Invalid script URL: ${url}`));
      return;
    }
    // Only allow scripts served over http(s) — blocks javascript:, data:, blob:, etc.
    if (!["https:", "http:"].includes(parsed.protocol)) {
      reject(new Error(`Blocked script with disallowed protocol: ${parsed.protocol}`));
      return;
    }
    // Optional allowlist: if provided, only permit scripts from trusted origins/hosts
    if (
      allowed_origins.length > 0 &&
      !allowed_origins.some(origin => parsed.origin === origin || parsed.hostname === origin)
    ) {
      reject(new Error(`Blocked script from untrusted origin: ${parsed.origin}`));
      return;
    }
    const safe_url = parsed.href;
    if (document.querySelector(`script[src="${safe_url}"]`)) {
      // Script already loaded
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = safe_url;
    script.onerror = e => reject(new Error(e?.message || `Failed to load script: ${safe_url}`));
    script.onload = () => resolve();
    document.body.appendChild(script);
    setTimeout(() => reject(new Error(`Script "${safe_url}" timed out`)), 8000);
  });
};

/**
 * Combines multiple class names into a single string, filtering out falsy values.
 * @param {...string} classes - The class names to combine.
 * @returns {string} A single string of class names.
 */
export const combineClasses = (...classes) => classes.filter(Boolean).join(" ");

/**
 * Defines test type names and their corresponding labels.
 */
const testTypeNames = [
  { value: bloodCholesterol, label: "Cholesterol Profile Test" },
  { value: bloodEnergy, label: "Energy Profile Test" },
  { value: bloodErectile, label: "Erectile Dysfunction Test" },
  { value: bloodGeneral, label: "General Health Test" },
  { value: bloodHeart, label: "Heart Profile Test" },
  { value: bloodMenopause, label: "Menopause Profile Test" },
  { value: bloodPolycystic, label: "Polycystic Ovary Syndrome (PCOS)" },
  { value: bloodThyroid, label: "Thyroid Profile Test" },
  { value: bloodVitamins, label: "Vitamins & Minerals Profile Test" },
  { value: bloodWeight, label: "Weight Management Test" },
  { value: bloodMaleSexHealth, label: "Male Sexual Health Test" },
  { value: bloodFemaleSexHealth, label: "Female Sexual Health Test" },
  { value: bloodVitaminB12, label: "Vitamin B12 Test" },
  { value: bloodTestosterone, label: "Testosterone Test" },
  { value: dnaWeight, label: "Weight Management DNA Test" },
  { value: dnaGlucose, label: "Glucose Management DNA Test" },
  { value: dnaIntolerances, label: "Intolerances & Sensitivities Management DNA Test" },
  { value: dnaHeart, label: "Heart Management DNA Test" },
  { value: dnaVitamins, label: "Vitamins & Minerals Management DNA Test" },
  { value: bootsThyroid, label: "Thyroid Profile Test" },
  { value: bootsCholesterol, label: "Cholesterol Profile Test" },
  { value: bootsEnergy, label: "Energy Profile Test" },
  { value: bootsGeneral, label: "General Health Test" },
  { value: bootsVitamins, label: "Vitamins & Minerals Profile Test" },
  { value: bootsWeight, label: "Weight Management Test" },
];

/**
 * Retrieves the label for a given test type.
 */

export const getTestName = type => {
  const testType = testTypeNames.find(
    testTypeName => testTypeName.value === type
  );
  return testType ? testType.label : "";
};

/**
 * Formats a date string to 'DD/MM/YYYY' format.
 */
export const formatReportedDate = dateString => {
  if (dateString === " ") {
    return " ";
  }
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
};

export const formatReportedDateTime = dateString => {
  if (!dateString || dateString.trim() === "") {
    return " ";
  }

  return new Date(dateString)
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    })
    .replace(",", ""); // remove comma
};


/**
 * Formats a timestamp to 'DD/MM/YYYY' format using moment.js.
 */
export const format_date = ts => moment(ts).format("DD/MM/YYYY");

/**
 * Formats a timestamp to 'Do /MMM /YYYY' format and adds relative time.
 */
const DTF = "Do /MMM /YYYY"; // Define the date format string

/**
 * Formats a date of birth to include age in years.
 */
export const format_dt_ago = ts => {
  const m = moment(ts);
  return `${m.format(DTF)} (${m.fromNow()})`;
};

/**
 * Formats a date of birth to include age in years.
 */
export const format_dt_age = dob => {
  const dobMoment = moment(dob); // Used moment to parse the date of birth
  const currentMoment = moment(); // Get the current date using moment

  // Calculate the age
  const age = currentMoment.diff(dobMoment, "years");

  // Format the date of birth and return the result
  return `${dobMoment.format("DD/MM/YYYY")} (${age} years old)`;
};

/**
 * Converts a date string from MM/DD/YYYY format to YYYY-MM-DD format.
 */
export const formatDob = dateString => {
  const [month, day, year] = dateString.split("/");

  // Check if the input date is valid
  if (!month || !day || !year) {
    throw new Error("Invalid date format. Must be in MM/DD/YYYY format.");
  }

  // Convert to YYYY-MM-DD format
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

/**
 * Formats a barcode by cleaning and segmenting it into a specific pattern.
 */
export const formatBarcode = barcode => {
  let cleanedBarcode = barcode.replace(/[^a-zA-Z0-9]/g, ""); // Remove non-alphanumeric characters

  let firstSegment = cleanedBarcode
    .slice(0, 3)
    .toUpperCase()
    .replace(/[^A-Z]/g, ""); // Capitalize and remove non-alphabets
  let secondSegment = cleanedBarcode
    .slice(3, 6)
    .replace(/[^a-zA-Z]/g, "")
    .toUpperCase(); // Remove non-alphabets
  let remainingSegment = cleanedBarcode.slice(6, 10);

  if (remainingSegment) {
    return `${firstSegment}-${secondSegment}-${remainingSegment}`;
  } else if (secondSegment) {
    return `${firstSegment}-${secondSegment}`;
  } else {
    return firstSegment;
  }
};
export function formatSecondaryBarcode(barcode) {
  // Split the barcode into two parts: before the dash and after the dash
  const parts = barcode?.split("-");

  // Check if the barcode format is correct (i.e., it has exactly two parts)
  if (parts?.length === 2) {
    // Convert the second part to lowercase and return the formatted barcode
    return `${parts[0]}-${parts[1]?.toLowerCase()}-${parts[2]}`;
  }

  // Return the original barcode if it doesn't match the expected format
  return barcode;
}

export const statusMapping = {
  registered: { variant: "primary", label: "Registered" },
  created: { variant: "primary", label: "Registered" },
  processing: { variant: "yellow", label: "Processing" },
  in_transit: { variant: "powder-pink", label: "In Transit" },
  processing_consultation: { variant: "mint", label: "Processing" },
  processing_mint: { variant: "mint", label: "Processing" },
  processing_raspberry: { variant: "raspberry", label: "Processing" },
  results_ready: { variant: "dark-green", label: "Results Ready" },
  activating: { variant: "grey-lilac", label: "Activating" },
  rejected: { variant: "raspberry", label: "Rejected" }
};

export function formatString(input) {
  // Replace hyphens with spaces and capitalize each word
  const words = input.split("-");
  return words
    .map((word, index) => {
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1); // Capitalize the first word
      }
      return word.toLowerCase(); // Keep the rest in lowercase
    })
    .join(" "); // Join the words back with spaces               // Join the words back with spaces
}

// Define a function to handle the asynchronous update of meet_details
export const TimeFormatter = (date, time) => {
  return new Promise(resolve => {
    if (!date || !time) {
      resolve("");
      return;
    }

    // Combine date and time into a single string
    const combinedDateTimeStr = `${date}T${time}`;

    // Parse the combined string into a Date object
    const dateObj = new Date(combinedDateTimeStr);

    // Check if the dateObj is valid
    if (Number.isNaN(dateObj.getTime())) {
      resolve("");
      return;
    }

    // Extract components
    const year = dateObj.getUTCFullYear();
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getUTCDate()).padStart(2, "0");
    const hours = String(dateObj.getUTCHours()).padStart(2, "0");
    const minutes = String(dateObj.getUTCMinutes()).padStart(2, "0");
    const seconds = String(dateObj.getUTCSeconds()).padStart(2, "0");
    const milliseconds = String(dateObj.getUTCMilliseconds()).padStart(3, "0");

    // Get timezone offset
    const offset = -dateObj.getTimezoneOffset();
    const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(
      2,
      "0"
    );
    const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, "0");
    const sign = offset >= 0 ? "+" : "-";

    // Construct the formatted datetime string
    const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}${sign}${offsetHours}:${offsetMinutes}`;

    //resolve the promise
    resolve(formattedDateTime);
  });
};

export const TEST_TYPES = {
  "blood-cholesterol": bloodCholesterol,
  "blood-thyroid": bloodThyroid,
  "blood-energy": bloodEnergy,
  "blood-female-sex-health": bloodFemaleSexHealth,
  "blood-general": bloodGeneral,
  "blood-heart": bloodHeart,
  "blood-male-sex-health": bloodMaleSexHealth,
  "blood-menopause": bloodMenopause,
  "blood-polycystic": bloodPolycystic,
  "blood-vitamins": bloodVitamins,
  "blood-weight": bloodWeight,
  "blood-fertility": bloodFertility,
  "blood-progesterone": bloodProgresterone,
  "boots-blood-testosterone": bloodTestosterone,
  "blood-testosterone": bloodTestosterone,
  "blood-vitamin-b12": bloodVitaminB12,
  "boots-blood-vitamin-b12": bloodVitaminB12,
  weight: dnaWeight,
  glucose: dnaGlucose,
  intolerances: dnaIntolerances,
  "boots-blood-thyroid": bootsThyroid,
  "boots-blood-cholesterol": bootsCholesterol,
  "boots-blood-energy": bootsEnergy,
  "boots-blood-general": bootsGeneral,
  "boots-blood-vitamins": bootsVitamins,
  "boots-blood-weight": bootsWeight,
};

export const URL_TO_TEST_TYPE = {
  cholesterol: TEST_TYPES["blood-cholesterol"],
  "energy-profile": TEST_TYPES[bloodEnergy],
  "general-health": TEST_TYPES[bloodGeneral],
  "heart-profile": TEST_TYPES[bloodHeart],
  thyroid: TEST_TYPES[bloodThyroid],
  "vitamins-minerals": TEST_TYPES[bloodVitamins],
  "weight-management": TEST_TYPES[bloodWeight],
  "vitamin-b12": TEST_TYPES[bloodVitaminB12],
  "testosterone": TEST_TYPES[bloodTestosterone],
  weight: TEST_TYPES[dnaWeight],
  glucose: TEST_TYPES[dnaGlucose],
  heart: TEST_TYPES[dnaHeart],
  vitamins: TEST_TYPES[dnaVitamins],
  intolerances: TEST_TYPES[dnaIntolerances]
};

export function getTestTypeValueFromUrl(urlValue) {
  // Check if the urlValue is a key in TEST_TYPES
  if (Object.values(TEST_TYPES).includes(urlValue)) {
    return urlValue; // Return as is if it matches a TEST_TYPES value
  }

  // Otherwise, check URL_TO_TEST_TYPE mapping
  return URL_TO_TEST_TYPE[urlValue] || "unknown"; // Return 'unknown' if no match is found
}

export const TEST_TYPE_TO_NAME = {
  [TEST_TYPES[bloodCholesterol]]: "Cholesterol Profile Test",
  [TEST_TYPES[bloodEnergy]]: "Energy Profile Test",
  [TEST_TYPES[bloodGeneral]]: "General Health Test",
  [TEST_TYPES[bloodHeart]]: "Heart Profile Test",
  [TEST_TYPES[bloodThyroid]]: "Thyroid Profile Test",
  [TEST_TYPES[bloodVitamins]]: "Vitamins & minerals Profile Test",
  [TEST_TYPES[bloodWeight]]: "Weight Management Test",
  [TEST_TYPES[bloodMaleSexHealth]]: "Male Sexual Health Test",
  [TEST_TYPES[bloodFemaleSexHealth]]: "Female Sexual Health Test",
  [TEST_TYPES[bloodMenopause]]: "Menopause Profile Test",
  [TEST_TYPES[bloodPolycystic]]: "Polycystic Ovary Syndrome (PCOS) Test",
  [TEST_TYPES[bloodVitaminB12]]: "Vitamin B12 Test",
  [TEST_TYPES[bloodTestosterone]]: "Testosterone Test",
  [TEST_TYPES[bootsThyroid]]: "Thyroid Profile Test",
  [TEST_TYPES[bootsCholesterol]]: "Cholesterol Profile Test",
  [TEST_TYPES[bootsEnergy]]: "Energy Profile Test",
  [TEST_TYPES[bootsGeneral]]: "General Health Test",
  [TEST_TYPES[bootsVitamins]]: "Vitamins & minerals Profile Test",
  [TEST_TYPES[bootsWeight]]: "Weight Management Test",
};

export function getTestNameFromType(testType) {
  return TEST_TYPE_TO_NAME[testType] || "Unknown Test";
}
export const handleChangeWithContext = (
  e,
  props,
  setInputValue,
  setFieldValue,
  handleTrackingNumberChange,
  auth
) => {
  const { value } = e.target;

  if (props.name === "code" && (value.toLowerCase().startsWith("m") || value.toLowerCase().startsWith("bg") || value.toLowerCase().startsWith("r") || value.toLowerCase().startsWith("p"))) {
    const formattedValue = formatBarcode(value);
    setInputValue(formattedValue);
    setFieldValue(props.name, formattedValue);
  } 
  else if (props.name === "code" && (value.toLowerCase().startsWith("co") || value.toLowerCase().startsWith("bt"))) {
    const formattedValue = dnaFormatBarcode(value);
    setInputValue(formattedValue);
    setFieldValue(props.name, formattedValue);
  } else if (props.name === "trackingNumber") {
    handleTrackingNumberChange(e, props, setInputValue, setFieldValue);
  } else {
    setInputValue(value); // Update local state
    setFieldValue(props.name, value); // Update Formik's state
  }
};

// Function to convert a string with underscores or dashes to title case
export const as_title = s => {
  return (s || "")
    .replace(/[_-]/g, " ") // Replace underscores or dashes with spaces
    .replace(/(?:_|^)\w/g, l => l.toUpperCase()) // Capitalize after underscores or word boundaries
    .replace(/\bB12\b/i, "B12") // Ensure B12 is always properly capitalized
    .replace(/\bdna\b/gi, "DNA"); // Replace 'dna' with 'DNA' (case-insensitive)
};

// Function to format the value based on the provided format
export function format_value(value, fmt, choices) {
  if (typeof value === "boolean" && !fmt) {
    const icon = value ? faCheck : faTimes;
    return <FontAwesomeIcon icon={icon} />;
  }

  if (!value) {
    return <span className="text-muted">&mdash;</span>;
  }

  if (choices) {
    const selected = choices.find(c => c.value === value);
    return selected ? selected.label : value;
  }

  switch (fmt) {
    case "datetime":
      return format_dt_ago(value);
    case "date":
      return format_date(value);
    case "inline-code":
      return <code className="inline-code">{value}</code>;
    case "as-title":
      return as_title(value);
    case "as-country":
      if (globalThis.country_lookup) {
        return globalThis.country_lookup[value] || value;
      }
      return value;
    case "money":
      return `£${value.toFixed(2)}`;
    default:
      if (typeof fmt === "function") {
        return fmt(value);
      } else {
         throw new TypeError(`Unknown fmt "${fmt}"`);
      }
  }
}

// Function to format the phone number
const formatPhoneNumber = input => {
  if (input === "") {
    return ""; // Allow empty input
  }

  // Remove non-numeric characters except +
  let formattedNumber = input.replace(/[^\d]/g, ""); // Only allow digits

  // Automatically add +44 if not present
  if (!formattedNumber.startsWith("44") && formattedNumber.length > 0) {
    formattedNumber = "44" + formattedNumber; // Prepend 44 if not present
  }

  // Format the number to +44 XXXX XXXX XXXX
  if (formattedNumber.startsWith("44")) {
    const prefix = "+44 ";
    const areaCode = formattedNumber.substring(2, 6); // 4444
    const mainNumber = formattedNumber.substring(6, 13); // Remaining digits
    formattedNumber = prefix + areaCode + (mainNumber ? " " + mainNumber : "");
  }

  return formattedNumber;
};

export const formatItemName = (name) => {
  if (name === "Vitamin-C Deficiency") {
    return "Vitamin C Deficiency";
  }
  return name;
};


export const handleTrackingNumberChange = (
  e,
  props,
  setInputValue,
  setFieldValue
) => {
  if (!props.readOnly && !props.disabled) {
    let value = e.target.value;
    let cleanedValue = value.replace(/[^a-zA-Z\d]/g, "");

    let firstSegment = cleanedValue
      .slice(0, 2)
      .replace(/[^A-Za-z]/g, "")
      .toUpperCase();
    let secondSegment = cleanedValue.slice(2, 6).replace(/\D/g, "");
    let thirdSegment = cleanedValue.slice(6, 10).replace(/\D/g, "");
    let fourthSegmentPart1 = cleanedValue.slice(10, 11).replace(/\D/g, "");
    let fourthSegmentPart2 = cleanedValue
      .slice(11, 13)
      .replace(/[^A-Za-z]/g, "")
      .toUpperCase();

    let formattedValue = firstSegment;
    if (secondSegment) formattedValue += ` ${secondSegment}`;
    if (thirdSegment) formattedValue += ` ${thirdSegment}`;
    if (fourthSegmentPart1 || fourthSegmentPart2) {
      formattedValue += ` ${fourthSegmentPart1}${fourthSegmentPart2}`;
    }

    setInputValue(formattedValue); // Update local state
    setFieldValue(props.name, formattedValue); // Update Formik's state
  }
};

function dnaFormatBarcode(value) {
  // Remove any non-alphabetic characters from the first part
  const letters = value
    .slice(0, 5)
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase();

  // Remove any non-numeric characters from the second part
  const digits = value.slice(5, 10).replace(/\D/g, "");

  // Return formatted value
  return letters + digits;
}


export const YoutubeEmbed = ({ embedId }) => (
  <div className="ratio ratio-16x9">
    <iframe
      title={`YouTube video player - ${embedId}`}
      src={`https://www.youtube.com/embed/${embedId}`}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen />
  </div>
);

YoutubeEmbed.propTypes = {
  embedId: PropTypes.string.isRequired,
};


/**
 * handleValidation function
 *
 * Validates the entered values based on the current path. It checks if the entered secondary barcode
 * and its type are appropriate for the selected test type (blood, sexual health, or DNA).
 *
 * @param {Object} values - The form values entered by the user.
 * @returns {string} An error message if validation fails, or an empty string if it passes.
 */

export const handleValidation = (values, currentPath) => {
  if (currentPath.includes("/enter-barcode/blood")) {
    if (values.secondary_barcode_id) {
      if (values.secondary_barcode_type !== "lavender") {
        return "To proceed with activating your Blood Test Kit, please ensure that you have selected the appropriate option on the activation page.";
      }
    } else if (
      values.secondary_barcode_type &&
      values.secondary_barcode_type !== "lavender"
    ) {
      return "To proceed with activating your Blood Test Kit, please ensure that you have selected the appropriate option on the activation page.";
    }
  } else if (currentPath.includes("/enter-barcode/sh")) {
    if (
      !values.secondary_barcode_id ||
      values.secondary_barcode_type !== "urine"
    ) {
      return "To proceed with activating your Sexual Health Test Kit, please ensure that you have selected the appropriate option on the activation page.";
    }
  } else if (currentPath.includes("/enter-barcode/dna")) {
    if (values.secondary_barcode_id) {
      return "Secondary_barcode must be null.";
    }
  }
  return "";
};

export const format_date_reports = ts => moment(ts).format("Do MMM YYYY");

/**
 * Returns an object containing the 'min' and 'max' date attributes for a date picker.
 * The 'min' will be 15 days ago from today, and the 'max' will be today's date.
 * @returns {Object} - Object with min and max date in 'YYYY-MM-DD' format.
 */
export function getDateRangeForPicker() {
  // Get today's date
  const today = new Date();

  // Format today's date to 'YYYY-MM-DD'
  const todayFormatted = today.toISOString().split("T")[0];

  // Get the date 15 days ago
  const fifteenDaysAgo = new Date();
  fifteenDaysAgo.setDate(today.getDate() - 14);

  // Format the date 15 days ago to 'YYYY-MM-DD'
  const fifteenDaysAgoFormatted = fifteenDaysAgo.toISOString().split("T")[0];

  return {
    min: fifteenDaysAgoFormatted, // Allow only dates from 15 days ago
    max: todayFormatted // Disable future dates (up to today)
  };
}

export const today = new Date(); // Format today's date to YYYY-MM-DD
export const todayFormatted = today.toISOString().split("T")[0];
const fifteenDaysAgo = new Date();
fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 14);
export const previousFifteenDays = fifteenDaysAgo.toISOString().split("T")[0]; // Format the date 15 days ago

export const getRouteCategory = currentPath => {
  const routeCategories = [
    {
      category: "dashboard", patterns: [/^\/$/,
        /\/pkb\//
      ]
    },
    {
      category: "activation",
      patterns: [
        /^\/activate$/,
        /^\/register-kit$/,
        /^\/test-kits\/resume\/(?:PG|RG|BG|MG|CO|BT)[A-Za-z0-9-]-[A-Za-z0-9-]+\/?$/,
        /^\/tests\/bloods\/activate$/,
        /^\/tests\/sh\/activate$/,
        /^\/tests\/dna\/activate$/,
        /^\/confirm-details\/(?:PG|RG|BG|MG|CO|BT|RL)[A-Za-z0-9-]+\/?$/,
        /^\/consent\/(?:PG|RG|BG|MG|CO|BT|RL)[A-Za-z0-9-]+\/?$/,
        /^\/secondary-barcode\/(?:PG|RG|BG|MG|CO|BT|RL)[A-Za-z0-9-]+\/?$/,
        /^\/essential-tips\/[^/]+$/,
        /^\/essential-tips\/(?:PG|RG|BG|MG|CO|BT|RL)[A-Za-z0-9-]+\/?$/,
        /^\/time-administered\/(?:PG|RG|BG|MG|CO|BT|RL)[A-Za-z0-9-]+\/?$/,
        /^\/assessment-details\/[^/]+$/,
        /^\/assessment\/[^/]+\/1$/,
        /^\/assessment\/[^/]+\/2$/,
        /^\/assessment\/[^/]+\/3$/,
        /^\/assessment\/[^/]+\/4$/,
        /^\/assessment\/[^/]+\/5$/,
        /^\/assessment\/[^/]+\/6$/,
        /^\/assessment\/[^/]+\/7$/,
        /^\/sh-consent\/[^/]+$/,
        /^\/wellness-collect-sample\/[^/]+$/,
        /^\/wellness-assessment\/[^/]+$/
      ]
    },
    {
      category: "my-results",
      patterns: [
        /^\/tests$/,
        /^\/test-kits\/[^/]+\/$/,
        /^\/my-reports\/blood\/[^/]+$/,
        /^\/bloods-modal$/,
        /^\/wellness-report\/[^/]+$/,
        /^\/assessment\/wellness\/[^/]+$/
      ]
    },
    {
      category: "my-appointments",
      patterns: [
        /^\/appointments$/,
      ]
    },
    { category: "account", patterns: [/^\/account$/] },
    {
      category: "appointment-booking",
      patterns: [
        /^\/bloods\/boots\/[^/]+$/,
        /^\/bloods\/boots\/registration\/[^/]+$/,
        /^\/bloods\/boots\/[^/]+\/booking\/consent\/[^/]+$/,
        /^\/bloods\/boots\/[^/]+\/booking\/confirm-details\/[^/]+\/?$/,
        /^\/bloods\/boots\/start-assessment\/[^/]+\/[^/]+$/,
        /^\/bloods\/boots\/assessment\/[^/]+\/[^/]+\/1$/,
        /^\/bloods\/boots\/assessment\/[^/]+\/[^/]+\/2$/,
        /^\/bloods\/boots\/assessment\/[^/]+\/[^/]+\/3$/,
        /^\/bloods\/boots\/assessment\/[^/]+\/[^/]+\/4$/,
        /^\/bloods\/boots\/assessment\/[^/]+\/[^/]+\/5$/,
        /^\/bloods\/boots\/assessment\/[^/]+\/[^/]+\/6$/,
        /^\/bloods\/boots\/assessment\/[^/]+\/[^/]+\/7$/,
        /^\/bloods\/boots\/assessment-details\/[^/]+\/[^/]+$/,
        /^\/bloods\/boots\/success-page\/[^/]+\/[^/]+$/
      ]
    }
  ];

  for (const { category, patterns } of routeCategories) {
    if (patterns.some(pattern => pattern.test(currentPath))) {
      return category;
    }
  }
  return "unknown"; // Default if no match
};

export const desktopMenu = {
  hcp_menu: [
    {
      to: "/",
      name: "My dashboard",
      icon: dashboard,
      category: "dashboard"
    },
    {
      to: "/activate",
      name: "Register test",
      icon: assessment,
      category: "activation"
    },
    {
      to: "/bloods/boots/placeholder",
      name: "Register booking",
      icon: booking,
      category: "appointment-booking"
    },
    {
      to: "/appointments",
      name: "Appointments",
      icon: appointment,
      category: "my-appointments"
    },
    {
      to: "/tests",
      name: "My results",
      icon: results,
      category: "my-results"
    },
    {
      to: "/account",
      name: "Account",
      icon: settings,
      category: "account"
    },
    {
      to: "https://myhealthchecked.com/help",
      name: "Support",
      icon: support
    }
  ],
  neo_menu: [
    {
      to: "/",
      name: "My dashboard",
      icon: dashboard,
      category: "dashboard"
    },
    {
      to: "/activate",
      name: "Register test",
      icon: assessment,
      category: "activation"
    },
    {
      to: "/appointments",
      name: "Appointments",
      icon: appointment,
      category: "my-appointments"
    },
    {
      to: "/tests",
      name: "My results",
      icon: results,
      category: "my-results"
    },
    {
      to: "/account",
      name: "Account",
      icon: settings,
      category: "account"
    },
    {
      to: "https://myhealthchecked.com/help",
      name: "Support",
      icon: support
    }
  ]
}

export const mobileMenu = {
  mobile_hcp_menu: [
    {
      ...desktopMenu.hcp_menu[0],
      name: "Home",
    },
    {
      ...desktopMenu.hcp_menu[4],
      name: "My Results",
    },
    {
      ...desktopMenu.hcp_menu[1],
      name: "Register",
    },
    // {
    //   ...desktopMenu.hcp_menu[2],
    //   name: "Booking",
    // },
    // {
    //   ...desktopMenu.hcp_menu[3],
    //   name: "Appt",
    // },
    {
      ...desktopMenu.hcp_menu[5],
      name: "Account",
    },
    // {
    //   ...desktopMenu.hcp_menu[5],
    //   name: "Support",
    // },
    // {
    //   to: "",
    //   name: "Log out",
    //   icon: null
    // }
  ],
  mobile_neo_menu: [
    {
      ...desktopMenu.neo_menu[0],
      name: "Home",
    },
    {
      ...desktopMenu.neo_menu[3],
      name: "My Results",
    },
    {
      ...desktopMenu.neo_menu[1],
      name: "Register",
    },
    // {
    //   ...desktopMenu.neo_menu[2],
    //   name: "Appt",
    // },
    {
      ...desktopMenu.neo_menu[4],
      name: "Account",
    },
  ]
}

// Keeping for future reference if anything breaks
// export const stripHTML = (input) => {
//   if (typeof input !== 'string') return input;
//   return input.replace(/<[^>]*>/g, '').trim();
// };

export const stripHTML = (input) => {
  if (typeof input !== 'string') return input;
  if (input.length > 10000) return input; // Prevent excessive input

  return input.replace(/<[^>]{0,200}>/g, '').trim();
};

// utils/calculateNextTestCountdown.js

export const calculateNextTestCountdown = (publishedDate, resultValue, testType = "Vitamin B12") => {
  if (!publishedDate || !resultValue) return null;

  const testIntervals = {
    "Vitamin B12": [
      { category: "High", range: { min: 150, max: Infinity }, weeks: 12 },
      { category: "Normal", range: { min: 70, max: 150 }, months: 12 },
      { category: "Low-Normal", range: { min: 37.5, max: 70 }, weeks: 12 },
      { category: "Low", range: { min: 0, max: 37.5 }, weeks: 12 },
    ],
  };
    const config = testIntervals[testType];
    if (!config) return null;

  const interval = config.find(t => resultValue > t.range.min && resultValue <= t.range.max);

  const published = new Date(publishedDate);
  const dueDate = getDueDate(published, interval);
  if (!dueDate) return null;

  const now = new Date();
  const diffMs = dueDate - now;
  if (diffMs <= 0) return expiredCountdown(interval);

  return interval.months
    ? {
        ...getMonthsCountdown(now, dueDate),
        dueDate,
      }
    : {
        ...getWeeksCountdown(diffMs),
        dueDate,
      };
};

// --- Helper functions ---
const getDueDate = (published, interval) => {
  if (interval.months) {
    const due = new Date(published);
    due.setMonth(published.getMonth() + interval.months);
    return due;
  }
  if (interval.weeks) {
    return new Date(published.getTime() + interval.weeks * 7 * 24 * 60 * 60 * 1000);
  }
  return null;
};

const expiredCountdown = (interval) => ({
  months: 0,
  weeks: 0,
  mode: interval.months ? "months" : "weeks",
});

const getMonthsCountdown = (now, dueDate) => {
  let monthsLeft =
    (dueDate.getFullYear() - now.getFullYear()) * 12 +
    (dueDate.getMonth() - now.getMonth());
  if (dueDate.getDate() > now.getDate()) monthsLeft += 1;

  return {
    months: Math.max(monthsLeft, 0),
    weeks: 0,
    mode: "months",
  };
};

const getWeeksCountdown = (diffMs) => {
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const weeksLeft = Math.ceil(daysLeft / 7);

  return {
    months: 0,
    weeks: Math.max(weeksLeft, 0),
    mode: "weeks",
  };
};

export const isSafeUrl = (url) => {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' || parsed.protocol === 'mailto:';
  } catch {
    return false;
  }
};

// Blocks script-executing URL schemes (javascript:, vbscript:) and non-image
// data: payloads while still allowing normal paths, http(s) URLs, and bundler-
// inlined data:image/* SVGs, so bundled/local image assets keep working unchanged.
export const isSafeImageSrc = (src) => {
  if (!src) return false;
  const trimmed = String(src).trim();
  if (/^(javascript|vbscript):/i.test(trimmed)) return false;
  if (/^data:/i.test(trimmed)) return /^data:image\//i.test(trimmed);
  return true;
};